import { useCallback } from "react";

import type { MagentoShippingMethod } from "@jti/magento-types";
import { useSelector } from "@jti/store/reduxHooks";
import type { CheckoutGTMFlowType, TrackingEventsAdditionalData } from "@jti/tracker";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { buildCommerceBaseData, getTemplateName } from "@checkout-v2/shared/utils";

import { useCartItems } from "../useCartItems";
import { useCartPrices } from "../useCartPrices";

type UseGtmShippingInfoReturn = {
  trackGtmShippingInfo: (
    method: MagentoShippingMethod,
    checkoutFlowType: CheckoutGTMFlowType | null,
    shippingInfoPrefilled: boolean
  ) => void;
};

export const useGtmShippingInfo = (): UseGtmShippingInfoReturn => {
  const sharedData = getTemplateName("checkout");
  const { data: cartItems } = useCartItems();
  const { data: cartPrices } = useCartPrices();
  const shippingSection = useSelector((state) => state.checkoutV2.sectionsState.shippingSection);

  const trackGtmShippingInfo = useCallback(
    (method: MagentoShippingMethod, checkoutFlowType: CheckoutGTMFlowType | null, shippingInfoPrefilled: boolean) => {
      if (!cartItems || !cartPrices || !checkoutFlowType) {
        return;
      }

      const checkoutAddShippingInfoData: TrackingEventsAdditionalData[TrackingEvents.TrackingCheckoutAddShippingInfo] =
        {
          section: sharedData.section,
          template_name: sharedData.template_name,
          ...buildCommerceBaseData({ items: cartItems, prices: cartPrices }),
          shipping_tier: method.carrierCode,
          checkout_flow_type: checkoutFlowType,
          shipping_info_prefilled: shippingInfoPrefilled,
          shipping_info_edited: shippingSection === "editing",
        };

      dispatchTrackingEvent(TrackingEvents.TrackingCheckoutAddShippingInfo, checkoutAddShippingInfoData);
    },
    [cartItems, cartPrices, sharedData, shippingSection]
  );

  return { trackGtmShippingInfo };
};
